import { FlexBox } from '@helloself/hello-self-ui';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '../../../app/hooks';
import { selectUser } from '../../auth/selectors';
import { useGetConnectionsForUserQuery } from '../../connections/model/query/connection';
import { selectConnectionsProfileByExpertId } from '../../connections/model/slice/selectors';
import { ProfileProps } from '../containers/ProfilePageRoute';
import { useGetInsurersForExpertQuery, useGetProfilePhotoQuery } from '../model/profile';
import useProfileRecordEvent from './hooks/useProfileRecordEvent';
import ListWithIcon from './list/ListWithIcon';
import { AboutMeTile } from './tiles/about-me/AboutMeTile';
import { BasicInfoTile } from './tiles/basic-info/BasicInfoTile';
import { ConnectionsTile } from './tiles/connections/ConnectionsTile';
import { InsurerTile } from './tiles/insurers/InsurerTile';
import { LanguagesTile } from './tiles/languages/LanguagesTile';
import { OtherAreasTile } from './tiles/other-areas/OtherAreasTile';
import { ReferNowTile } from './tiles/refer-now/ReferNowTile';
import { TopSpecialitiesTile } from './tiles/top-specialities/TopSpecialitiesTile';
import { TypesOfTherapyTile } from './tiles/types-of-therapy/TypesOfTherapyTile';
import { Toolbar } from './Toolbar';

export interface ExpertProfileProps extends ProfileProps {
    background: {
        backgroundImage: string;
        backgroundPosition: string;
    };
}

export default function ExpertProfile({ profile, isLoadingProfiles, background }: ExpertProfileProps) {
    const { cognitoId: expertId } = useParams<{ cognitoId: string }>();
    const { recordProfileEvent } = useProfileRecordEvent();

    const connectionsProfile = useSelector((state) => selectConnectionsProfileByExpertId(state, expertId));

    // eslint-disable-next-line prefer-const
    let { data: profilePicture, error: errorLoadingProfilePhoto } = useGetProfilePhotoQuery(expertId, {
        skip: !expertId,
    });

    const {
        data: insurers,
        isLoading: isLoadingInsurers,
        error: errorLoadingInsurers,
    } = useGetInsurersForExpertQuery(expertId);

    const { data: connections, isLoading: isLoadingConnections } = useGetConnectionsForUserQuery(expertId);
    const user = useAppSelector(selectUser);
    const isConnected =
        (connections?.followedBy || []).findIndex((connection) => connection.userId === user.cognitoId) > -1;

    if (errorLoadingProfilePhoto) {
        //TODO: should we record this in amplitude or datadog?
        profilePicture = undefined;
    }

    const handleBackClick = () => {
        recordProfileEvent('Clicked Back', {
            'Clicked from Location': 'Profile Page',
            'Profile Type': 'expert',
        });
    };

    return (
        <FlexBox
            {...background}
            backgroundRepeat="no-repeat"
            backgroundSize="contain"
            width="100%"
            minHeight="100%"
            height="max-content"
            justifyContent="center"
            data-testid="profile"
            position="relative"
        >
            <FlexBox margin="62px 0px" flexDirection="column" width="920px" gap="8px">
                <Toolbar isOwnProfile={false} onBackClick={handleBackClick} />

                <FlexBox gap="24px">
                    <FlexBox flexDirection="column" gap="16px" width="330px">
                        <BasicInfoTile
                            expertId={expertId}
                            externalId={connectionsProfile?.externalId}
                            profile={profile}
                            profilePictureUrl={profilePicture}
                            isLoading={isLoadingProfiles}
                            isConnected={isConnected}
                        />

                        <ReferNowTile profile={profile} externalId={connectionsProfile?.externalId} />
                        <ConnectionsTile following={connections?.following || []} isLoading={isLoadingConnections} />
                        <InsurerTile insurers={insurers} isLoading={isLoadingInsurers} error={errorLoadingInsurers} />
                        <LanguagesTile languages={profile?.languages || []} isLoading={isLoadingProfiles} />
                    </FlexBox>
                    <FlexBox flexDirection="column" gap="16px" width="566px">
                        <AboutMeTile aboutMe={profile?.aboutMe} isLoading={isLoadingProfiles} />
                        <TopSpecialitiesTile topSpecialisms={profile?.topSpecialisms} isLoading={isLoadingProfiles} />
                        <OtherAreasTile otherTopics={profile?.otherTopics} isLoading={isLoadingProfiles}>
                            Other areas I have experience in
                        </OtherAreasTile>
                        <TypesOfTherapyTile
                            treatmentApproaches={profile?.treatmentApproaches}
                            isLoading={isLoadingProfiles}
                        />

                        {!!profile?.qualifications && profile.qualifications.length > 0 && (
                            <ListWithIcon
                                header={'Qualifications'}
                                entries={profile?.qualifications}
                                isLoadingEntries={isLoadingProfiles}
                            />
                        )}
                        {!!profile?.experience && profile.experience.length > 0 && (
                            <ListWithIcon
                                header={'Work Experience'}
                                entries={profile?.experience}
                                isLoadingEntries={isLoadingProfiles}
                            />
                        )}
                        {!!profile?.education && profile.education.length > 0 && (
                            <ListWithIcon
                                header={'Education'}
                                entries={profile?.education}
                                isLoadingEntries={isLoadingProfiles}
                            />
                        )}
                    </FlexBox>
                </FlexBox>
            </FlexBox>
        </FlexBox>
    );
}
